import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createMuiTheme, ThemeProvider, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Container from 'common/Container';
import landingApiService from '../../../services/landingApi';
import {
  Who,
  Why,
  Benefits,
  Values,
} from '../TNOMediaKitCreator/components';
import AreYouReady from '../TNOMediaKitCreator/components/AreYouReady';
import {
  MercosurPodcastHero,
  ProductionPhases,
  ProductionServices,
  DeliverablesMetrics,
  InvestmentSummary,
} from './components';
import {
  mercosurMediaLabWho,
  mercosurMediaLabWhy,
  mercosurMediaLabBenefits,
  mercosurMediaLabValues,
  mercosurMediaLabAreYouReady,
} from './mercosurPodcastMediaLabContent';

const mercosurTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#e65100',
    },
  },
});

const MercosurPodcastPage = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'), { defaultMatches: true });
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await landingApiService.getProposalBySlugStatic('mercosur-podcast');
        if (!cancelled) setProposal(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!proposal) return;
    if (proposal.metaTitle) document.title = proposal.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc && proposal.metaDescription) desc.setAttribute('content', proposal.metaDescription);
    return () => {
      document.title = 'TNO Media Kit | Anuncia con Nosotros';
    };
  }, [proposal]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography color="textSecondary">Cargando propuesta...</Typography>
      </Box>
    );
  }

  if (!proposal?.content) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography color="error">Propuesta no encontrada.</Typography>
      </Box>
    );
  }

  const content = proposal.content;

  return (
    <Box>
      <Container>
        <MercosurPodcastHero hero={content.hero} themeMode={themeMode} />
      </Container>

      <Container>
        <Who themeMode={themeMode} content={mercosurMediaLabWho} forceContent />
      </Container>

      <Container
        sx={{
          backgroundImage: 'url("/images/millennials_stream.jpg")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'top',
          backgroundSize: 'cover',
          minHeight: '50vh',
        }}
        borderRadius={isSm ? 4 : 0}
      />

      <Container>
        <Why content={mercosurMediaLabWhy} />
      </Container>

      <Container>
        <Benefits themeMode={themeMode} content={mercosurMediaLabBenefits} />
      </Container>

      <Container>
        <Values content={mercosurMediaLabValues} />
      </Container>

      <Container>
        <ProductionPhases section={content.tnoEcosystem} />
      </Container>

      <Container>
        <ProductionServices section={content.productsExplained} />
      </Container>

      <Container>
        <DeliverablesMetrics metrics={content.metrics} section={content.metricsSection} />
      </Container>

      <Container>
        <InvestmentSummary summary={content.investmentSummary} />
      </Container>

      <Container>
        <AreYouReady content={mercosurMediaLabAreYouReady} />
      </Container>
    </Box>
  );
};

const LandingMediaKitMercosurPodcast = (props) => (
  <ThemeProvider theme={mercosurTheme}>
    <MercosurPodcastPage {...props} />
  </ThemeProvider>
);

LandingMediaKitMercosurPodcast.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingMediaKitMercosurPodcast;
